/**
 * Side-sheet listing saved-but-not-yet-promoted properties. Clicking a row
 * opens the StrategyPickerDialog; on confirm, the selection promotes the
 * row into the pipeline via `useSavedProperties().promoteToPipeline`.
 *
 * "Eligible" = pipeline_properties rows where `strategy IS NULL`. That's
 * how we differentiate "heart-saved" from "in pipeline."
 */
import { Bath, Bed, Heart, Plus, Ruler } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { fmtMoney, parseMoney } from "@/lib/calculators";
import type { Strategy } from "@/types/pipeline";

import { StrategyPickerDialog } from "./StrategyPickerDialog";
import { useState } from "react";

interface NewDealDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful promotion so the caller can close + refresh. */
  onPromoted?: () => void;
}

interface CandidateRow {
  id: string;
  property_id: string;
  property_data: Record<string, unknown>;
  strategy?: Strategy | null;
  pipeline_id?: string | null;
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "";
  return String(s)
    .split(" ")
    .map((word) =>
      word.length <= 2 && /^[A-Z]+$/.test(word.replace(/[,.]/g, ""))
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
}

export function NewDealDrawer({
  open,
  onOpenChange,
  onPromoted,
}: NewDealDrawerProps) {
  const { savedProperties, isLoading, promoteToPipeline, watchlist } =
    useSavedProperties();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingPropertyId, setPendingPropertyId] = useState<string | null>(
    null,
  );

  // Eligible = heart-saved (no pipeline yet). Once promoted to a pipeline
  // these rows already live in the kanban and shouldn't show here.
  const candidates = (savedProperties as unknown as CandidateRow[]).filter(
    (r) => !r.pipeline_id,
  );

  function handlePick(strategy: Strategy) {
    if (!pendingPropertyId) return;
    promoteToPipeline({
      propertyId: pendingPropertyId,
      strategy,
      pipelineId: watchlist?.id ?? null,
    }).then((ok) => {
      if (ok) {
        setPickerOpen(false);
        setPendingPropertyId(null);
        onOpenChange(false);
        onPromoted?.();
      }
    });
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[460px] max-w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>New Deal</SheetTitle>
            <SheetDescription>
              Pick a saved property to add to the pipeline.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex flex-col gap-2 overflow-y-auto pb-24">
            {isLoading && (
              <>
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </>
            )}

            {!isLoading && candidates.length === 0 && (
              <div className="my-12 text-center text-sm text-muted-foreground">
                <Heart
                  className="mx-auto mb-3 h-9 w-9 text-muted-foreground/60"
                  aria-hidden
                />
                <div>No eligible saved properties</div>
                <div className="mt-1 text-xs text-muted-foreground/80">
                  Save properties on the Find page, then come back here.
                </div>
              </div>
            )}

            {!isLoading &&
              candidates.map((row) => {
                const data = row.property_data ?? {};
                const addr1 =
                  (data.address as string | undefined) ??
                  (data.addr1 as string | undefined) ??
                  "";
                const city = (data.city as string | undefined) ?? "";
                const state = (data.state as string | undefined) ?? "";
                const zip = (data.zip as string | undefined) ?? "";
                const addr2 =
                  city || state || zip
                    ? `${city}${state ? `, ${state}` : ""}${zip ? ` ${zip}` : ""}`
                    : ((data.addr2 as string | undefined) ?? "");
                const priceRaw =
                  (data.price as string | number | undefined) ?? null;
                const price = priceRaw != null ? parseMoney(priceRaw) : null;
                const bd =
                  (data.bedrooms as number | undefined) ??
                  (data.bd as number | undefined) ??
                  null;
                const ba =
                  (data.bathrooms as number | undefined) ??
                  (data.ba as number | undefined) ??
                  null;
                const sqft =
                  (data.sqft as string | number | undefined) ?? null;
                const img =
                  (data.imageUrl as string | undefined) ??
                  (data.img as string | undefined) ??
                  null;

                return (
                  <div
                    key={row.id}
                    className="flex gap-3 rounded-lg border border-border bg-muted/40 p-2.5"
                  >
                    {img && (
                      <div
                        className="h-[76px] w-24 flex-shrink-0 rounded-md bg-cover bg-center"
                        style={{ backgroundImage: `url('${img}')` }}
                        aria-hidden
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-extrabold">
                        {price != null ? fmtMoney(price) : ""}
                      </div>
                      <div className="truncate text-xs font-bold">
                        {titleCase(addr1) || "Unknown"}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {titleCase(addr2)}
                      </div>
                      <div className="mt-1 flex gap-2.5 text-[11px] text-muted-foreground">
                        {bd ? (
                          <span className="inline-flex items-center gap-1">
                            <Bed className="h-3 w-3" aria-hidden /> {bd} bd
                          </span>
                        ) : null}
                        {ba ? (
                          <span className="inline-flex items-center gap-1">
                            <Bath className="h-3 w-3" aria-hidden /> {ba} ba
                          </span>
                        ) : null}
                        {sqft ? (
                          <span className="inline-flex items-center gap-1">
                            <Ruler className="h-3 w-3" aria-hidden /> {sqft}{" "}
                            sqft
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setPendingPropertyId(row.property_id);
                        setPickerOpen(true);
                      }}
                      className="self-center"
                    >
                      <Plus className="h-3 w-3" aria-hidden /> Add
                    </Button>
                  </div>
                );
              })}
          </div>
        </SheetContent>
      </Sheet>

      <StrategyPickerDialog
        open={pickerOpen}
        onOpenChange={(o) => {
          setPickerOpen(o);
          if (!o) setPendingPropertyId(null);
        }}
        onSelect={handlePick}
      />
    </>
  );
}
